import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Sidebar from "./Sidebar";
import AceBackground from "../shared/AceBackground";
import LogoutConfirmModal from "../layout/LogoutConfirmModal";

import { getCurrentUser, logoutAdmin } from "../../utils/auth";
import { useAppPreferences } from "../../context/AppPreferencesContext";

const NOTIFICATION_KEY = "ac_admin_notifications";
const MOBILE_BREAKPOINT = 980;
const SIDEBAR_WIDTH_OPEN = "280px";
const SIDEBAR_WIDTH_COLLAPSED = "92px";
const SIDEBAR_WIDTH_MOBILE = "0px";

function getDefaultNotifications(t) {
  return [
    {
      id: "n1",
      title: t("shell.defaultNotifications.feedbackTitle"),
      sub: t("shell.defaultNotifications.feedbackSub"),
      tag: t("shell.defaultNotifications.feedbackTag"),
      path: "/feedback",
      unread: true,
    },
    {
      id: "n2",
      title: t("shell.defaultNotifications.streakTitle"),
      sub: t("shell.defaultNotifications.streakSub"),
      tag: t("shell.defaultNotifications.streakTag"),
      path: "/stats",
      unread: true,
    },
    {
      id: "n3",
      title: t("shell.defaultNotifications.stressTitle"),
      sub: t("shell.defaultNotifications.stressSub"),
      tag: t("shell.defaultNotifications.stressTag"),
      path: "/notifications",
      unread: false,
    },
  ];
}

function readNotifications(fallbackItems) {
  try {
    const raw = JSON.parse(localStorage.getItem(NOTIFICATION_KEY) || "null");

    if (!Array.isArray(raw) || raw.length === 0) {
      return fallbackItems;
    }

    const hasLegacyKeys = raw.some(
      (item) =>
        String(item?.title || "").includes("shell.") ||
        String(item?.sub || "").includes("shell.") ||
        String(item?.tag || "").includes("shell.")
    );

    return hasLegacyKeys ? fallbackItems : raw;
  } catch {
    return fallbackItems;
  }
}

function writeNotifications(items) {
  localStorage.setItem(NOTIFICATION_KEY, JSON.stringify(items));
}

function markAllAsRead(items) {
  return items.map((item) => ({
    ...item,
    unread: false,
  }));
}

function translateKnownTitle(title, t) {
  const map = {
    Dashboard: "shell.dashboard",
    "Nutzer:innen & Klassen": "shell.usersClasses",
    Inhalte: "shell.content",
    Statistiken: "shell.stats",
    Benachrichtigungen: "shell.notifications",
    Regeln: "shell.rules",
    Feedback: "shell.feedback",
    "Konto & Einstellungen": "shell.settings",
  };

  return map[title] ? t(map[title]) : title ?? "AceTime";
}

function BellMenuIcon() {
  return (
    <svg viewBox="0 0 24 24" className="ac-topbar-svg" aria-hidden="true">
      <path
        d="M12 4.5a4 4 0 0 1 4 4v2.2c0 1 .3 2 .9 2.8l1 1.4c.5.8 0 1.9-1 1.9H7.1c-1 0-1.5-1.1-1-1.9l1-1.4c.6-.8.9-1.8.9-2.8V8.5a4 4 0 0 1 4-4Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M10 18.5a2.2 2.2 0 0 0 4 0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function UserMenuIcon() {
  return (
    <svg viewBox="0 0 24 24" className="ac-topbar-svg" aria-hidden="true">
      <circle
        cx="12"
        cy="8.2"
        r="3.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M5.8 18c1.1-3.1 3.5-4.8 6.2-4.8s5.1 1.7 6.2 4.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MenuToggleIcon({ open = false }) {
  if (open) {
    return (
      <svg viewBox="0 0 24 24" className="ac-menu-svg" aria-hidden="true">
        <path
          d="M4 7h16"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M4 12h16"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M4 17h16"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className="ac-menu-svg" aria-hidden="true">
      <path
        d="M7 4v16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M12 4v16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M17 4v16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TopbarIconButton({
  children,
  onClick,
  active = false,
  title,
  expanded = false,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      aria-haspopup="menu"
      aria-expanded={expanded}
      className={`ac-topbar-actionbtn ${active ? "is-active" : ""}`}
    >
      {children}
    </button>
  );
}

function NotificationMenu({ items, onViewAll, onOpenItem, onMarkAllRead, t }) {
  const unreadCount = items.filter((item) => item.unread).length;

  const unreadText =
    unreadCount > 0
      ? t(
          unreadCount === 1
            ? "shell.newNotificationSingular"
            : "shell.newNotificationPlural",
          { count: unreadCount }
        )
      : t("shell.noNewNotifications");

  return (
    <div className="ac-menu-panel ac-notification-menu ac-notification-menu-compact">
      <div className="ac-menu-head">
        <div>
          <div className="ac-menu-title">{t("shell.notificationsMenuTitle")}</div>
          <div className="ac-menu-subtitle">
            {t("shell.notificationsMenuSubtitle")}
          </div>
        </div>

        {unreadCount > 0 && (
          <button
            type="button"
            onClick={onMarkAllRead}
            className="ac-menu-textbtn"
          >
            {t("shell.markAllRead")}
          </button>
        )}
      </div>

      <div className="ac-menu-meta">{unreadText}</div>

      <div className="ac-menu-list">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onOpenItem(item)}
            className={`ac-menu-row ac-menu-row-clickable ${
              item.unread ? "is-unread" : ""
            }`}
          >
            <div className="ac-menu-row-icon">
              <BellMenuIcon />
            </div>

            <div className="ac-menu-row-copy">
              <div className="ac-menu-row-title">{item.title}</div>
              <div className="ac-menu-row-sub">{item.sub}</div>
            </div>

            <div className="ac-menu-row-right">
              {item.unread && <span className="ac-menu-row-statusdot" />}
              <span className="ac-menu-tag">{item.tag}</span>
            </div>
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={onViewAll}
        className="ac-primarybtn ac-menu-fullbtn ac-menu-fullbtn-compact"
      >
        {t("shell.viewAllNotifications")}
      </button>
    </div>
  );
}

function ProfileMenu({
  userName,
  userEmail,
  userRole,
  onGoAccount,
  onGoNotifications,
  onLogout,
  t,
}) {
  return (
    <div className="ac-menu-panel ac-profile-menu">
      <div className="ac-profile-head">
        <div className="ac-profile-avatar">
          <UserMenuIcon />
        </div>

        <div className="min-w-0">
          <div className="ac-menu-title truncate">{userName}</div>
          <div className="ac-menu-subtitle truncate">{userEmail}</div>
          <div className="ac-profile-role">{userRole}</div>
        </div>
      </div>

      <div className="ac-menu-divider" />

      <div className="ac-profile-actions">
        <button type="button" onClick={onGoAccount} className="ac-menu-linkbtn">
          {t("shell.myProfile")}
        </button>

        <button
          type="button"
          onClick={onGoNotifications}
          className="ac-menu-linkbtn"
        >
          {t("shell.notifications")}
        </button>
      </div>

      <div className="ac-menu-divider" />

      <button
        type="button"
        onClick={onLogout}
        className="ac-menu-linkbtn is-dangerless"
      >
        {t("shell.logout")}
      </button>
    </div>
  );
}

export default function DashboardLayout({ title, right = null, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useAppPreferences();

  const topbarMenusRef = useRef(null);

  const defaultNotifications = useMemo(() => getDefaultNotifications(t), [t]);
  const translatedTitle = useMemo(() => translateKnownTitle(title, t), [title, t]);

  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("ac_sidebar_collapsed") === "1";
  });

  const [isMobile, setIsMobile] = useState(
    () => window.innerWidth <= MOBILE_BREAKPOINT
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  const [logoutOpen, setLogoutOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifications, setNotifications] = useState(() =>
    readNotifications(defaultNotifications)
  );

  const unreadCount = useMemo(() => {
    return notifications.filter((item) => item.unread).length;
  }, [notifications]);

  const currentUser = getCurrentUser();
  const userName = currentUser?.name || "AceTime Admin";
  const userEmail = currentUser?.email || "admin@acetime.at";
  const userRole = currentUser?.role || "Admin";

  const menuIsOpen = isMobile ? mobileOpen : !collapsed;

  // Menü-Status
  const closeMenus = useCallback(() => {
    setNotificationsOpen(false);
    setProfileOpen(false);
  }, []);

  const closeAllOverlays = useCallback(() => {
    closeMenus();
    setMobileOpen(false);
  }, [closeMenus]);

  const applySidebarWidth = useCallback((mobile, isCollapsed) => {
    const width = mobile
      ? SIDEBAR_WIDTH_MOBILE
      : isCollapsed
      ? SIDEBAR_WIDTH_COLLAPSED
      : SIDEBAR_WIDTH_OPEN;

    document.documentElement.style.setProperty("--ac-sidebar-w", width);
  }, []);

  // Persistenz
  useEffect(() => {
    writeNotifications(notifications);
  }, [notifications]);

  useEffect(() => {
    localStorage.setItem("ac_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  // Sidebar / Breakpoint
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      applySidebarWidth(mobile, collapsed);

      if (!mobile) {
        setMobileOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [collapsed, applySidebarWidth]);

  useEffect(() => {
    applySidebarWidth(isMobile, collapsed);
  }, [isMobile, collapsed, applySidebarWidth]);

  // Route-Wechsel
  useEffect(() => {
    closeAllOverlays();
  }, [location.pathname, closeAllOverlays]);

  // Klick außerhalb
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!topbarMenusRef.current) return;
      if (!topbarMenusRef.current.contains(event.target)) {
        closeMenus();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeMenus]);

  // Escape
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeAllOverlays();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeAllOverlays]);

  // Body lock auf Mobile
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // Aktionen
  const openLogout = () => setLogoutOpen(true);
  const closeLogout = () => setLogoutOpen(false);

  const doLogout = () => {
    logoutAdmin();
    navigate("/login", { replace: true });
  };

  const handleMenuButton = () => {
    if (isMobile) {
      setMobileOpen((value) => !value);
      return;
    }

    setCollapsed((value) => !value);
  };

  const markAllNotificationsRead = () => {
    setNotifications((prev) => markAllAsRead(prev));
  };

  const toggleNotificationsMenu = () => {
    setNotificationsOpen((prev) => {
      const next = !prev;

      if (next) {
        setProfileOpen(false);
        setNotifications((items) => markAllAsRead(items));
      }

      return next;
    });
  };

  const toggleProfileMenu = () => {
    setProfileOpen((prev) => !prev);
    setNotificationsOpen(false);
  };

  const openNotificationItem = (item) => {
    setNotifications((prev) =>
      prev.map((entry) =>
        entry.id === item.id ? { ...entry, unread: false } : entry
      )
    );

    navigate(item.path);
  };

  const goToNotificationsOverview = () => {
    markAllNotificationsRead();
    navigate("/notifications");
  };

  return (
    <div className="min-h-screen bg-ac-offwhite text-ac-text relative">
      <div className="ac-bg-layer pointer-events-none" aria-hidden="true">
        <AceBackground />
      </div>

      <Sidebar
        collapsed={isMobile ? false : collapsed}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        onRequestLogout={openLogout}
      />

      <div className="ac-shell">
        <header className="ac-topbar">
          <div className="ac-topbar-inner">
            <div className="ac-topbar-left">
              <button
                type="button"
                onClick={handleMenuButton}
                className="ac-iconbtn ac-menu-btn"
                aria-label={t("shell.sidebar")}
                aria-expanded={menuIsOpen}
                title={t("shell.sidebar")}
              >
                <MenuToggleIcon open={menuIsOpen} />
              </button>

              <div className="min-w-0">
                <h1 className="ac-title truncate">{translatedTitle}</h1>
                <div className="ac-topbar-subline">{t("shell.administration")}</div>
              </div>
            </div>

            <div className="ac-topbar-right" ref={topbarMenusRef}>
              {right}

              <div className="ac-topbar-actionwrap">
                <TopbarIconButton
                  onClick={toggleNotificationsMenu}
                  active={notificationsOpen}
                  expanded={notificationsOpen}
                  title={t("shell.notifications")}
                >
                  <BellMenuIcon />
                </TopbarIconButton>

                {unreadCount > 0 && <span className="ac-topbar-dot" />}
              </div>

              <TopbarIconButton
                onClick={toggleProfileMenu}
                active={profileOpen}
                expanded={profileOpen}
                title={t("shell.profile")}
              >
                <UserMenuIcon />
              </TopbarIconButton>

              {notificationsOpen && (
                <NotificationMenu
                  items={notifications}
                  onViewAll={goToNotificationsOverview}
                  onOpenItem={openNotificationItem}
                  onMarkAllRead={markAllNotificationsRead}
                  t={t}
                />
              )}

              {profileOpen && (
                <ProfileMenu
                  userName={userName}
                  userEmail={userEmail}
                  userRole={userRole}
                  onGoAccount={() => navigate("/settings")}
                  onGoNotifications={() => navigate("/notifications")}
                  onLogout={openLogout}
                  t={t}
                />
              )}
            </div>
          </div>
        </header>

        <main className="ac-main ac-page-enter">
          <div className="ac-pop">{children}</div>
        </main>
      </div>

      <LogoutConfirmModal
        open={logoutOpen}
        onClose={closeLogout}
        onConfirm={doLogout}
      />
    </div>
  );
}